import { useEffect, useRef, useState } from "react";

import chevronDown from "@/../../hypetrain/static/img/chevron-down.svg";
import chevronUp from "@/../../hypetrain/static/img/chevron-up.svg";
import styled from "@emotion/styled";
import { animate } from "motion";

import LazyImg from "@/components/helpers/ui/LazyImg";
import { Flex } from "@/components/layout/flexbox";
import Clickable from "@/components/pieces/interaction/Clickable";
import { INNER_BORDER_COLOR, MAINTEXT_COLOR } from "@/css/constants";

function FaqItem({ question, answer, preOpen }) {
  const [isOpen, setOpen] = useState(preOpen || false);
  const contentRef = useRef(null);

  useEffect(() => {
    if (contentRef.current) {
      const contentHeight = contentRef.current.scrollHeight;

      if (isOpen) {
        animate(
          contentRef.current,
          {
            height: contentHeight,
            opacity: 1,
          },
          {
            duration: 0.3,
            ease: [0.4, 0, 0.2, 1],
          },
        );
      } else {
        animate(
          contentRef.current,
          {
            height: 0,
            opacity: 0,
          },
          {
            duration: 0.3,
            ease: [0.4, 0, 0.2, 1],
          },
        );
      }
    }
  }, [isOpen]);

  return (
    <CollapsibleItem
      className={`${isOpen ? "item-open custom-faq-item" : "custom-faq-item"}`}
    >
      <Clickable onClick={() => setOpen(!isOpen)}>
        <Flex
          className="collapsible-item-header"
          justifyContent="space-between"
          alignItems="center"
        >
          <div className="a-center flex-row">
            <div className="collapsible-item-lead">{question}</div>
          </div>
          <div className="caret-container">
            <div className="item-caret-open">
              <LazyImg src={chevronUp} />
            </div>
            <div className="item-caret-closed">
              <LazyImg src={chevronDown} />
            </div>
          </div>
        </Flex>
      </Clickable>
      <div ref={contentRef} className="collapsible-item-content">
        <div
          className="content-inner"
          dangerouslySetInnerHTML={{ __html: answer }}
        />
      </div>
    </CollapsibleItem>
  );
}

function FaqItems({ faqItems, preOpen = false }) {
  return (
    <FaqContainer className="faq-container">
      {faqItems.map(({ question, answer }) => (
        <FaqItem
          question={question}
          answer={answer}
          preOpen={preOpen}
          key={question}
        />
      ))}
    </FaqContainer>
  );
}

const TEXT_COLOR = IS_EXEC ? MAINTEXT_COLOR : "#748096";

const FaqContainer = styled("div")`
  border: 1px solid ${INNER_BORDER_COLOR};
  border-radius: 6px;
  background-color: #fff;
`;

const CollapsibleItem = styled("div")`
  text-align: left;
  border-bottom: 1px solid ${INNER_BORDER_COLOR};

  .collapsible-item-header {
    color: ${TEXT_COLOR};
    cursor: pointer;
    padding: 24px;
    transition: all 0.2s ease;

    &:hover {
      color: #000;
      background: rgba(0, 0, 0, 0.01);
    }
  }

  .collapsible-item-content {
    height: 0;
    overflow: hidden;
    opacity: 0;
    will-change: height;
  }

  .content-inner {
    padding: 4px 24px 24px;
    font-size: 1.1em;
  }

  .collapsible-item-lead {
    font-size: 1.2em;
  }

  .caret-container {
    transition: transform 0.3s ease;
  }

  .item-caret-open {
    display: none;
  }

  &.item-open {
    .collapsible-item-header {
      cursor: default;
      color: #000;
      font-weight: 500;
    }

    .item-caret-open {
      display: block;
    }

    .item-caret-closed {
      display: none;
    }

    .caret-container {
      transform: rotate(180deg);
    }
  }

  &:hover .collapsible-item-header {
    color: #000;
  }

  &:last-child {
    border-bottom: 0;
  }
`;

export default FaqItems;
